import React, { forwardRef } from 'react';
import { POSTER_SCHOOL_LOGO } from './BoardResultsPosterCard';
import { optionalSubjectLabel } from '../utils/boardResultSubject';
import './BoardResultsTop5Poster.css';

export const POSTER_SCHOOL_WEBSITE = 'www.fast.edupage.org';
export const POSTER_SCHOOL_PHONE = '0321 - 4469104';

function ordinalRank(rank) {
  const n = Number(rank);
  if (!Number.isFinite(n) || n < 1) return '';
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function formatPercentage(row) {
  if (row?.percentage_label) return String(row.percentage_label).replace(/\s/g, '');
  if (row?.percentage != null) return `${Number(row.percentage).toFixed(2)}%`;
  return '';
}

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function posterVariant(count) {
  const n = Number(count) || 5;
  if (n >= 20) {
    return {
      layout: 'top20',
      badge: 'TOP 20',
      title: 'TOP 20 POSITIONS',
      id: 'top20',
      hideRank: true,
      hidePct: true
    };
  }
  if (n >= 12) {
    return { layout: 'top12', badge: 'TOP 12', title: 'TOP 12 POSITIONS', id: 'top12' };
  }
  return { layout: 'top5', badge: 'TOP 5', title: 'TOP 5 POSITIONS', id: 'top5' };
}

const BoardResultsTop5Poster = forwardRef(function BoardResultsTop5Poster(
  { rows = [], photoUrls = {}, level, part, year, count = 5 },
  ref
) {
  const examLine = [level, part, year].filter(Boolean).join(' ');
  const variant = posterVariant(count);

  return (
    <div
      className={`br-poster br-poster-top5 br-poster-${variant.layout}`}
      ref={ref}
      data-poster-id={variant.id}
    >
      <div className="br-top5-deco" aria-hidden="true">
        <span className="br-top5-deco-yellow" />
        <span className="br-top5-deco-blue" />
      </div>

      <div className="br-top5-badge">{variant.badge}</div>
      <img
        src={POSTER_SCHOOL_LOGO}
        alt="The Fast School"
        className="br-top5-logo"
        crossOrigin="anonymous"
      />

      <div className="br-top5-header">
        <div className="br-top5-kicker">THE FAST SCHOOL</div>
        <h1 className="br-top5-title">{variant.title}</h1>
        {examLine && <div className="br-top5-sub">{examLine}</div>}
      </div>

      <div className="br-top5-list">
        {rows.map((row, idx) => {
          const rankLabel = ordinalRank(row?.rank_no) || `${idx + 1}`;
          const pct = formatPercentage(row);
          const marks = row?.total_marks != null ? String(row.total_marks) : '';
          const roll = row?.roll_no ? String(row.roll_no) : '';
          const subjectLabel = optionalSubjectLabel(row);
          const photoUrl = photoUrls[row.id];
          const place = Number(row?.rank_no) || idx + 1;
          return (
            <div
              key={row.id || roll || idx}
              className={`br-top5-row is-place-${place}`}
            >
              <div className="br-top5-photo">
                {photoUrl ? (
                  <img src={photoUrl} alt="" crossOrigin="anonymous" />
                ) : (
                  <span>{initialsFromName(row?.student_name)}</span>
                )}
              </div>
              <div className="br-top5-info">
                {!variant.hideRank && <div className="br-top5-rank">{rankLabel}</div>}
                <div className="br-top5-body">
                  <div className="br-top5-name">{String(row?.student_name || '').toUpperCase()}</div>
                  {roll && (
                    <div className="br-top5-roll">
                      ROLL NO {roll}
                      {subjectLabel ? ` (${subjectLabel})` : ''}
                    </div>
                  )}
                  <div className="br-top5-meta">
                    {marks && <span className="br-top5-marks">{marks}</span>}
                    {!variant.hidePct && pct && <span className="br-top5-pct">{pct}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="br-top5-foot">
        <span>{POSTER_SCHOOL_WEBSITE}</span>
        <span>{POSTER_SCHOOL_PHONE}</span>
      </div>
    </div>
  );
});

export default BoardResultsTop5Poster;
